/**
 * useAcademia — lecciones de la persona según su rol (y roles extra) + su avance (guardado en el backend como preferencia, no por dispositivo).
 *
 * Una sola instancia por sesión: <AcademiaProvider> (montado en Layout) guarda el avance una vez y todas las pantallas
 * (Centro de ayuda, guía, tour automático) leen y escriben el mismo. Antes cada pantalla tenía su copia y podían pisarse.
 *
 * Avance por lección: { visto_at, completado_at, version, via, tour }.
 *   Completada = llegó al último paso del tour (via «tour») o tocó «Listo, lo entendí» en la guía (via «guia»).
 *   tour = { vistos, visibles, totales, at }: cuántos pasos vio, cuántos tenían elemento en su pantalla y cuántos tiene la lección.
 *   Si la lección subió de versión después de completarla, se muestra «Actualizado».
 *
 * Eventos (van por el mismo canal de métricas de navegación, con source «academia»):
 *   academia_tour_inicio · academia_tour_paso · academia_tour_fin · academia_guia_vista · academia_video_click
 */
import { createContext, createElement, useCallback, useContext, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext.jsx";
import { useUserPref } from "../hooks/useUserPref.js";
import { trackNav } from "../lib/nav-tracker.js";
import { leccionesParaRol, rolesDe } from "./rutas.js";

export const CLAVE_AVANCE = "academia_soa10";

export function registrarEvento(evento, leccion, rol, extra = "") {
  if (!leccion?.id) return;
  const q = `id=${encodeURIComponent(leccion.id)}&rol=${encodeURIComponent(rol || "")}&version=${leccion.version}${extra ? `&${extra}` : ""}`;
  trackNav(`/academia/${evento}?${q}`, "academia");
}

export function estadoDeLeccion(avance, leccion) {
  const av = avance?.[leccion.id];
  if (!av) return "nueva";
  if (av.completado_at) return (Number(av.version) || 1) < leccion.version ? "actualizada" : "completada";
  return av.visto_at ? "vista" : "nueva";
}

const AcademiaCtx = createContext(null);

// Sin proveedor (no debería pasar dentro de la app): una versión inerte, para que ninguna pantalla se caiga.
const VACIO = {
  rol: "", roles: [], lecciones: [], avance: {}, synced: false, estadoDe: () => "nueva",
  marcarVisto: () => {}, marcarCompletado: () => {}, registrarTour: () => {}, completadas: 0, siguiente: null,
};

export function AcademiaProvider({ children }) {
  const { miembro } = useAuth();
  const rol = miembro?.rol || "";
  const rolesExtra = miembro?.roles_extra;
  const [avance, setAvance, { synced } = {}] = useUserPref(CLAVE_AVANCE, {});
  const ref = useRef(avance);
  ref.current = avance;

  const roles = useMemo(() => rolesDe(rol, rolesExtra), [rol, rolesExtra]);
  const rolesClave = roles.join("|");
  const lecciones = useMemo(() => leccionesParaRol(roles), [rolesClave]);   // eslint-disable-line react-hooks/exhaustive-deps
  const estadoDe = useCallback((l) => estadoDeLeccion(avance, l), [avance]);

  // Se arma desde la última versión conocida (ref) para que los callbacks largos del tour no pisen un cambio reciente.
  const guardar = useCallback((leccion, cambios) => {
    const prev = ref.current?.[leccion.id] || {};
    const siguiente = { ...ref.current, [leccion.id]: { ...prev, ...cambios, version: leccion.version } };
    ref.current = siguiente;
    setAvance(siguiente);
  }, [setAvance]);

  const marcarVisto = useCallback((leccion) => {
    const prev = ref.current?.[leccion.id] || {};
    guardar(leccion, { visto_at: prev.visto_at || new Date().toISOString() });
  }, [guardar]);

  /** via: «tour» (llegó al último paso) o «guia» («Listo, lo entendí»). */
  const marcarCompletado = useCallback((leccion, { via = "guia" } = {}) => {
    const prev = ref.current?.[leccion.id] || {};
    const ahora = new Date().toISOString();
    guardar(leccion, { visto_at: prev.visto_at || ahora, completado_at: ahora, via });
  }, [guardar]);

  /** Resultado de un tour (completo o no): cuántos pasos vio. Si llegó al final, además queda completada. */
  const registrarTour = useCallback((leccion, { vistos, visibles, completado }) => {
    const prev = ref.current?.[leccion.id] || {};
    const ahora = new Date().toISOString();
    const cambios = {
      visto_at: prev.visto_at || ahora,
      tour: { vistos, visibles, totales: (leccion.pasos || []).length, at: ahora },
    };
    if (completado) { cambios.completado_at = ahora; cambios.via = "tour"; }
    guardar(leccion, cambios);
  }, [guardar]);

  const completadas = lecciones.filter(l => estadoDe(l) === "completada").length;
  const siguiente = lecciones.find(l => estadoDe(l) !== "completada") || null;

  const valor = useMemo(
    () => ({ rol, roles, lecciones, avance, synced, estadoDe, marcarVisto, marcarCompletado, registrarTour, completadas, siguiente }),
    [rol, roles, lecciones, avance, synced, estadoDe, marcarVisto, marcarCompletado, registrarTour, completadas, siguiente]
  );
  return createElement(AcademiaCtx.Provider, { value: valor }, children);
}

export function useAcademia() {
  return useContext(AcademiaCtx) || VACIO;
}
